import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Newspaper, Eye, Clock, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale/tr";
import { API } from "../lib/api";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import HeroSlider from "../components/HeroSlider";
import CategorySlider from "../components/CategorySlider";
import PopularNews from "../components/PopularNews";
import PopularNewsSidebar from "../components/PopularNewsSidebar";
import TrendingStories from "../components/TrendingStories";
import GoogleTrends from "../components/GoogleTrends";
import { useLang } from "../context/LangContext";
import { 
  MobileCategoryBar, 
  MobileHeroSlider, 
  MobileNewsCard, 
  MobileCategorySection,
  categoryConfig 
} from "../components/MobileHome";

const Home = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const searchParams = new URLSearchParams(location.search);
  const selectedCategory = searchParams.get('category');
  const searchFromUrl = searchParams.get('search');

  useEffect(() => {
    setLoading(true);
    fetchNews();
  }, [selectedCategory, searchFromUrl, lang]);

  const fetchNews = async () => {
    try {
      let url = `${API}/news?limit=30&language=${lang}`;
      if (selectedCategory) {
        url += `&category=${encodeURIComponent(selectedCategory)}`;
      }
      if (searchFromUrl) {
        url += `&search=${encodeURIComponent(searchFromUrl)}`;
      }
      const response = await axios.get(url);
      setNews(response.data);
    } catch (e) {
      console.error("Error fetching news:", e);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryConfig = (name) => {
    return categoryConfig.find(c => c.name === name) || { icon: '📰', color: 'text-amber-400' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <div className="animate-pulse text-amber-500">Yükleniyor...</div>
      </div>
    );
  }

  // Category Page
  if (selectedCategory) {
    const catConfig = getCategoryConfig(selectedCategory);
    return (
      <div className="min-h-screen bg-[#0a0a0b]" data-testid="category-page">
        <SEO 
          title={selectedCategory}
          description={`${selectedCategory} kategorisindeki en güncel haberler. Loft Press'te ${selectedCategory} haberleri.`}
        />
        <main className="max-w-7xl mx-auto px-4 py-6 lg:py-8">
          {/* Mobile Category Bar */}
          <MobileCategoryBar selectedCategory={selectedCategory} />
          
          {/* Desktop Breadcrumb */}
          <div className="hidden lg:block">
            <Breadcrumb items={[
              { label: "Ana Sayfa", href: "/" },
              { label: selectedCategory }
            ]} />
          </div>
          
          <div className="flex items-center justify-between mb-6 lg:mb-8">
            <div className="flex items-center gap-2 lg:gap-3">
              <span className="text-2xl lg:text-3xl">{catConfig.icon}</span>
              <h1 className={`text-xl lg:text-3xl font-bold ${catConfig.color}`}>{selectedCategory}</h1>
              <span className="text-zinc-500 text-sm">({news.length})</span>
            </div>
            <Link 
              to="/" 
              className="hidden lg:flex px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors items-center gap-2"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              Tüm Haberler
            </Link>
          </div>

          {news.length === 0 ? (
            <div className="text-center py-16 lg:py-20">
              <Newspaper className="w-12 h-12 lg:w-16 lg:h-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500">Bu kategoride haber bulunmuyor.</p>
            </div>
          ) : (
            <>
              {/* Mobile News Grid */}
              <div className="lg:hidden space-y-3">
                {news.slice(0, 2).map((item) => (
                  <MobileNewsCard key={item.id} item={item} featured />
                ))}
                {news.slice(2).map((item) => (
                  <MobileNewsCard key={item.id} item={item} />
                ))}
              </div>
              
              {/* Desktop News Grid */}
              <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {news.map((item) => (
                  <Link 
                    key={item.id} 
                    to={`/haber/${item.slug || item.id}`}
                    className="group bg-zinc-900/50 rounded-xl overflow-hidden border border-zinc-800/50 hover:border-amber-500/30 transition-all"
                    data-testid={`news-card-${item.id}`}
                  >
                    <div className="aspect-video relative overflow-hidden">
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=225&fit=crop';
                        }}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-white group-hover:text-amber-400 transition-colors line-clamp-2 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-zinc-400 text-sm line-clamp-2 mb-3">
                        {item.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {format(new Date(item.created_at), "d MMM yyyy", { locale: tr })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {item.views}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    );
  }

  // Home Page
  return (
    <div className="min-h-screen bg-[#0a0a0b]" data-testid="home-page">
      <SEO />
      <main className="max-w-7xl mx-auto px-4 py-4 lg:py-8">
        {news.length === 0 ? (
          <div className="text-center py-16 lg:py-20">
            <Newspaper className="w-12 h-12 lg:w-16 lg:h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">Henüz haber bulunmuyor.</p>
          </div>
        ) : (
          <>
            {/* Mobile Category Bar */}
            <MobileCategoryBar selectedCategory={null} />
            
            {/* Mobile Hero Slider */}
            <MobileHeroSlider news={news} />
            
            {/* Desktop Hero Section */}
            <div className="hidden lg:grid grid-cols-1 lg:grid-cols-4 gap-6 mb-10">
              <div className="lg:col-span-3">
                <HeroSlider news={news} />
              </div>
              <div className="hidden lg:block">
                <PopularNewsSidebar />
              </div>
            </div>

            {/* Mobile: Trending Stories Section */}
            <div className="lg:hidden mb-6">
              <TrendingStories />
            </div>

            {/* Mobile: Google Trends */}
            <div className="lg:hidden mb-6">
              <GoogleTrends />
            </div>

            {/* Mobile: Category Sections (Horizontal Scroll) */}
            <div className="lg:hidden">
              {categoryConfig.map((cat) => (
                <MobileCategorySection
                  key={cat.name}
                  category={cat.name}
                  icon={cat.icon}
                  color={cat.color}
                  translatedCategory={t(cat.key)}
                />
              ))}
            </div>

            {/* Mobile: Latest News List */}
            <div className="lg:hidden mt-6">
              <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
                <span className="w-1 h-5 bg-amber-500 rounded-full" />
                {t('latestNews')}
              </h3>
              <div className="space-y-2">
                {news.slice(5, 15).map((item) => (
                  <MobileNewsCard key={item.id} item={item} />
                ))}
              </div>
              
              {news.length > 15 && (
                <Link 
                  to="/?category=Gündem" 
                  className="mt-4 w-full flex items-center justify-center gap-2 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition-colors"
                >
                  Daha Fazla Haber
                  <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>

            {/* Desktop: Popular News */}
            <div className="hidden lg:block">
              <PopularNews />
            </div>

            {/* Desktop: Trending Stories */}
            <div className="hidden lg:block">
              <TrendingStories />
            </div>

            {/* Desktop: Google Trends */}
            <div className="hidden lg:block">
              <GoogleTrends />
            </div>

            {/* Desktop: Category Sliders */}
            <div className="hidden lg:block space-y-2">
              {categoryConfig.map((cat) => (
                <CategorySlider
                  key={cat.name}
                  category={cat.name}
                  icon={cat.icon}
                  color={cat.color}
                  translatedCategory={t(cat.key)}
                />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Home;
